/**
 * SimpleChatProvider - A completely rewritten chat provider that cannot cause Error #185
 * 
 * Design principles:
 * 1. NO hooks that could possibly suspend or throw
 * 2. NO direct context consumption that might not be ready
 * 3. Always provide a valid context value, even if empty
 * 4. Gradual initialization after mount
 */

import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { ConnectionStatus } from '../../contexts/ChatConnectionTypes';

// Simple context value that always exists
interface SimpleChatContextValue {
  connectionStatus: ConnectionStatus;
  isConnected: boolean;
  isConnecting: boolean;
  hasError: boolean;
  isStreaming: boolean;
  currentStream: string;
  messagesCount: number;
  reconnectAttempts: number;
  lastConnectedTime?: Date;
  isInitialized: boolean;
}

// Default value that's always safe
const defaultContextValue: SimpleChatContextValue = {
  connectionStatus: ConnectionStatus.DISCONNECTED,
  isConnected: false,
  isConnecting: false,
  hasError: false,
  isStreaming: false,
  currentStream: '',
  messagesCount: 0,
  reconnectAttempts: 0,
  lastConnectedTime: undefined,
  isInitialized: false,
};

// Create context with a default value so it never throws
const SimpleChatContext = createContext<SimpleChatContextValue>(defaultContextValue);

interface SimpleChatProviderProps {
  children: React.ReactNode;
}

/**
 * SimpleChatProvider - Provides chat context without any possibility of suspension
 */
export const SimpleChatProvider: React.FC<SimpleChatProviderProps> = ({ children }) => {
  // Local state - no external dependencies
  const [contextValue, setContextValue] = useState<SimpleChatContextValue>(defaultContextValue);
  const mountedRef = useRef(false);
  const initTimerRef = useRef<NodeJS.Timeout>();
  
  // Initialize after mount to avoid any synchronous issues
  useEffect(() => {
    mountedRef.current = true;
    console.log('[SimpleChatProvider] Mounted - initializing in 10ms');
    
    // Delay initialization to ensure we're fully mounted
    initTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      
      console.log('[SimpleChatProvider] Initialization complete');
      setContextValue(prev => ({
        ...prev,
        isInitialized: true,
        connectionStatus: ConnectionStatus.DISCONNECTED,
      }));
    }, 10);
    
    return () => {
      mountedRef.current = false;
      if (initTimerRef.current) {
        clearTimeout(initTimerRef.current);
      }
      console.log('[SimpleChatProvider] Unmounting');
    };
  }, []);
  
  // Gradually add auth and conversation data after initialization
  useEffect(() => {
    if (!contextValue.isInitialized) return;
    
    // Try to get auth token safely after a delay
    const authTimer = setTimeout(() => {
      if (!mountedRef.current) return;
      
      try {
        // Attempt to get auth from window/localStorage
        // This is safe because it doesn't use React context
        const storedToken = localStorage.getItem('auth_token');
        if (storedToken) {
          console.log('[SimpleChatProvider] Found auth token in localStorage');
          // In a real implementation, you'd update the connection status here
        }
      } catch (error) {
        console.log('[SimpleChatProvider] Could not retrieve auth token:', error);
      }
    }, 100);
    
    return () => clearTimeout(authTimer);
  }, [contextValue.isInitialized]);
  
  // Always provide a valid context value
  return (
    <SimpleChatContext.Provider value={contextValue}>
      {children}
    </SimpleChatContext.Provider>
  );
};

/**
 * Hook to use the simple chat context
 * This will NEVER throw or suspend
 */
export const useSimpleChatContext = (): SimpleChatContextValue => {
  const context = useContext(SimpleChatContext);
  // Because we provide a default value, context will never be undefined
  return context || defaultContextValue;
};

/**
 * Wrapper that provides both the simple context and a compatibility layer
 */
export const SimpleChatProviderWrapper: React.FC<SimpleChatProviderProps> = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  
  useEffect(() => {
    // Small delay to ensure everything is mounted
    const timer = setTimeout(() => {
      setIsReady(true);
      console.log('[SimpleChatProviderWrapper] Ready to render children');
    }, 1);
    
    return () => clearTimeout(timer);
  }, []);
  
  // During the brief initialization, show nothing (prevents any hook calls)
  if (!isReady) {
    return null;
  }
  
  return (
    <SimpleChatProvider>
      {children}
    </SimpleChatProvider>
  );
};

// For backward compatibility with ChatConnectionProvider
export const ChatConnectionProvider: React.FC<{ children: React.ReactNode; value: any }> = ({ children, value }) => {
  // Just pass through the children for now
  // The SimpleChatProvider above handles the actual context
  return <>{children}</>;
};

export default SimpleChatProviderWrapper;